/**
 * Universal PDF Report Generator for Timbrica tools (v2)
 *
 * Usage:
 *   window.__generatePDF({
 *     element: domElement,          // DOM element to capture (required)
 *     filename: 'report.pdf',      // Output filename
 *     title: 'Tool Report',        // Optional: adds header with title (clone mode only)
 *     orientation: 'portrait',     // 'portrait' or 'landscape'
 *     scale: 2,                    // html2canvas raster scale
 *     onBefore: fn,                // Optional: called before capture (set light styles) -> live-capture mode
 *     onAfter: fn,                 // Optional: called after capture settles (restore styles)
 *     onclone: fn(clonedDoc),      // Optional: html2canvas onclone hook
 *     strings: {                   // Optional localized messages (English defaults)
 *       libError, genError, blocked, saved
 *     },
 *   }) -> Promise<{ok:true, ms, kb} | {ok:false, code}>
 *        code: 'lib_load' | 'canvas_blocked' | 'blank_render' | 'gen_error'
 *        Never rejects — legacy fire-and-forget callers must not produce
 *        unhandledrejection noise. New callers branch on result.ok.
 *
 * v2 guarantees (the four failure classes this module owns):
 *   1. scrollX/scrollY:0 on every capture — html2canvas shifts the render by the
 *      page scroll otherwise, producing the "blank PDF" class (hearing-test es
 *      report 2026-06-10: 100% of saved PDFs were white because the results
 *      card is always below the fold when Download is clicked).
 *   2. Canvas bitmaps survive cloning — cloneNode(true) drops canvas pixel data,
 *      so clone-mode copies every painted <canvas> into the clone explicitly.
 *   3. Canvas-readback poisoning (Firefox resistFingerprinting / CanvasBlocker)
 *      is detected by a probe BEFORE rendering and after (blank check) — the
 *      user gets an honest localized error instead of a silently saved
 *      white/noise PDF.
 *   4. Delivery goes through window.__downloadFile (Android WebView / in-app
 *      browsers route via the download stash; also emits file-downloaded for
 *      tool_complete telemetry) instead of html2pdf's detached-anchor .save().
 *
 * Telemetry: feature_use {kind:'pdf_export', ms, kb} on success,
 *            feature_use {kind:'pdf_export_fail', reason} on failure.
 */
(function() {
'use strict';

var cdnBase = (document.querySelector('meta[name="cdn-base"]') || {}).content || '';

var DEFAULT_STRINGS = {
    libError: 'Failed to load PDF library',
    genError: 'PDF generation failed',
    blocked: 'Your browser is blocking image export (canvas protection). Allow canvas access or use the copy button instead.',
    saved: '',
    working: 'Preparing PDF...',
};

/* Resolved per call, not at load: this file is deferred and __commonStrings is
   written by an inline script, so today the order happens to work — but a
   load-time read would turn any future reshuffle of the layout into a silent
   drop back to English, with nothing failing. */
function commonString(key) {
    try { return (window.__commonStrings && window.__commonStrings[key]) || ''; } catch (e) { return ''; }
}

var _libPromise = null;
function loadHtml2pdf() {
    if (window.html2pdf) return Promise.resolve();
    if (_libPromise) return _libPromise;
    /* Source order is the layout's (window.__pdfLibSrc = cdn_chain()): our own
     * copy, our own second address, the external mirror last — on BOTH halves
     * of the site. Until 16.10.2026 intl went to jsdelivr first. The bare own
     * path is the net when a page has no list at all.
     *
     * Every attempt is bounded: a script tag that neither loads nor errors
     * used to hang the PDF button for good with no event at all (the class
     * asset-chain.js bounds for the other libraries — return #37). The bound
     * counts VISIBLE time, so a backgrounded tab is not a stall. */
    var OWN = '/vendor/html2pdf/html2pdf.bundle.min.js';
    var srcs = (Array.isArray(window.__pdfLibSrc) && window.__pdfLibSrc.length)
        ? window.__pdfLibSrc.slice()
        : [cdnBase + OWN];
    if (srcs.indexOf(OWN) < 0) srcs.push(OWN);
    var ATTEMPT_MS = 45000;
    _libPromise = new Promise(function(resolve, reject) {
        function tryNext(i) {
            if (i >= srcs.length) {
                _libPromise = null; // allow retry on a later click
                reject(new Error('html2pdf: all sources failed'));
                return;
            }
            var settled = false, cancel = null;
            function settle(ok) {
                if (settled) return;
                settled = true;
                if (cancel) { try { cancel(); } catch (e) {} }
                if (ok && window.html2pdf) {
                    if (i > 0) emit('pdf_lib_fallback', { index: i, url: String(srcs[i]).slice(0, 120) });
                    resolve();
                } else {
                    tryNext(i + 1);
                }
            }
            var s = document.createElement('script');
            s.src = srcs[i];
            s.onload = function() { settle(true); };
            s.onerror = function() { settle(false); };
            if (typeof window.__visibleTimeout === 'function') {
                cancel = window.__visibleTimeout(function() { settle(false); }, ATTEMPT_MS);
            } else {
                var h = setTimeout(function() { settle(false); }, ATTEMPT_MS);
                cancel = function() { clearTimeout(h); };
            }
            document.head.appendChild(s);
        }
        tryNext(0);
    });
    return _libPromise;
}

function emit(kind, payload) {
    try {
        if (!window.__ae) return;
        var p = { kind: kind };
        for (var k in payload) p[k] = payload[k];
        window.__ae('feature_use', p);
    } catch (e) {}
}

function fail(code, message) {
    var err = new Error(message || code);
    err.code = code;
    return err;
}

/**
 * Detect canvas-readback poisoning (Firefox privacy.resistFingerprinting,
 * CanvasBlocker, Brave farbling in aggressive mode). Draw two known colors,
 * read them back; RFP returns white/noise, fake-mode extensions return
 * shifted values. Tolerance ±8/channel keeps subtle farbling passing (it
 * doesn't destroy the render) while catching the destructive modes.
 */
function canvasReadbackPoisoned() {
    try {
        var c = document.createElement('canvas');
        c.width = 8; c.height = 8;
        var x = c.getContext('2d', { willReadFrequently: true });
        x.fillStyle = '#3366cc'; x.fillRect(0, 0, 8, 4);
        x.fillStyle = '#cc6633'; x.fillRect(0, 4, 8, 4);
        var d = x.getImageData(0, 0, 8, 8).data;
        function off(i, r, g, b) {
            return Math.abs(d[i] - r) > 8 || Math.abs(d[i + 1] - g) > 8 || Math.abs(d[i + 2] - b) > 8;
        }
        // sample one pixel per half
        return off(4 * (1 * 8 + 3), 0x33, 0x66, 0xcc) || off(4 * (6 * 8 + 3), 0xcc, 0x66, 0x33);
    } catch (e) {
        return true; // getImageData threw — readback unavailable
    }
}

/**
 * Cheap blank-render check on the produced canvas: downscale into a small
 * canvas and count non-white pixels. A real report (any text) is far above
 * 0.05%; a scroll-shifted/invisible render is ~0%.
 */
function canvasLooksBlank(canvas) {
    try {
        if (!canvas || !canvas.width || !canvas.height) return true;
        var w = 160, h = Math.max(1, Math.round(canvas.height * (160 / canvas.width)));
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        var x = c.getContext('2d', { willReadFrequently: true });
        x.fillStyle = '#ffffff'; x.fillRect(0, 0, w, h);
        x.drawImage(canvas, 0, 0, w, h);
        var d = x.getImageData(0, 0, w, h).data;
        var nonWhite = 0, n = w * h;
        for (var i = 0; i < d.length; i += 4) {
            if (d[i] < 245 || d[i + 1] < 245 || d[i + 2] < 245) nonWhite++;
        }
        return (nonWhite / n) < 0.0005;
    } catch (e) {
        return false; // can't judge — don't block the export
    }
}

/* -- page geometry --------------------------------------------------------
 * html2pdf lays the clone out inside a container exactly `pageSize.inner.width`
 * wide - for the default A4 with 10 mm margins that is 190 mm ~ 718 px. Every
 * measurement taken at any other width is a guess, and the guess this module
 * shipped was `width:800px`: 82 px, a tenth of every report, fell off the right
 * edge of every page in all 31 tools that call it. Nothing threw; the PDF just
 * ended mid-sentence.
 */
var PAGE_MM = {
    a3: [297, 420], a4: [210, 297], a5: [148, 210], a6: [105, 148],
    letter: [215.9, 279.4], legal: [215.9, 355.6], tabloid: [279.4, 431.8]
};
var MM_PX = 96 / 25.4;

function normalizeMargin(m) {
    if (typeof m === 'number') return [m, m, m, m];
    if (Array.isArray(m)) {
        if (m.length === 1) return [m[0], m[0], m[0], m[0]];
        if (m.length === 2) return [m[0], m[1], m[0], m[1]];
        if (m.length >= 4) return [m[0], m[1], m[2], m[3]];
    }
    return [0, 0, 0, 0];
}

/** Content width of one page, in millimetres - the same number html2pdf uses. */
function pageInnerMm(opts) {
    var fmt = (opts && opts.format) || 'a4';
    var dims = Array.isArray(fmt) ? fmt : PAGE_MM[String(fmt).toLowerCase()];
    if (!dims || dims.length < 2) dims = PAGE_MM.a4;
    var landscape = String((opts && opts.orientation) || 'portrait').charAt(0).toLowerCase() === 'l';
    var w = landscape ? dims[1] : dims[0];
    var raw = (opts && opts.margin !== undefined) ? opts.margin : [10, 10, 10, 10];
    var mg = normalizeMargin(raw);
    return Math.max(20, w - mg[1] - mg[3]);
}

/**
 * The container-query context the source element stood in.
 *
 * `@container` rules are scoped to an ANCESTOR that declares `container-type`.
 * A clone lifted out of the page has no such ancestor, so every one of those
 * rules silently stops matching - and the element does not fall back to the
 * narrow layout, it falls back to whatever the unqualified rule happens to say.
 * On /synastry that turned a 300 px chart wheel into an 800 px one that filled
 * the whole first page and pushed the reading itself onto the second.
 */
function containerChain(el) {
    var chain = [], p = el.parentElement;
    while (p && p !== document.documentElement) {
        var s = null;
        try { s = window.getComputedStyle(p); } catch (e) {}
        if (s && s.containerType && s.containerType !== 'normal') {
            chain.unshift({
                type: s.containerType,
                name: (s.containerName && s.containerName !== 'none') ? s.containerName : ''
            });
        }
        p = p.parentElement;
    }
    return chain;
}

/**
 * A <details> the reader left shut.
 *
 * html2canvas has no UA shadow DOM: it paints the children of a CLOSED
 * <details> anyway, at the position the summary alone reserved - so the folded
 * block lands on top of whatever follows it. /synastry printed its aspect list,
 * its house overlay and its method note stacked into one illegible pile. A
 * report is read on paper, where nothing can be unfolded, so the fix is to open
 * them: the content is then both present and in its own space.
 */
/**
 * Decoration that hangs out of its box sideways.
 *
 * A glow is drawn as an absolutely positioned pseudo-element pushed past its
 * owner (`.lg-app::before{inset:-40px -40px 0}` in partials/liquid-glass, and
 * 20 more such rules across the tools). On screen `overflow-x:clip` hides it —
 * but it still counts in `scrollWidth`, which is what fitWideContent reads. The
 * 12 study tools build their report root as `.lg-app`: measured 16.10.2026 on
 * /ru/calculus-solver, scrollWidth 710 against 670 available, so the WHOLE
 * report was scaled to 0.944, and the page-break spacers html2pdf inserts
 * inside a scaled subtree came out short — the last card was cut in two and
 * page 4 carried only its title.
 *
 * On paper such a layer is nothing but a tint, so it is switched off in the
 * copy: only a pseudo that is absolutely placed AND sticks out to the left or
 * the right of its owner. A check mark inside a custom checkbox, a counter, an
 * icon in the flow — none of these qualify and all of them stay.
 */
var OVERHANG_STYLE = '[data-pdf-nodecor~="b"]::before,[data-pdf-nodecor~="a"]::after{display:none!important}';
function dropOverhangingDecor(root) {
    var marked = 0;
    try {
        var nodes = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
        for (var i = 0; i < nodes.length; i++) {
            var el = nodes[i], flags = '';
            for (var p = 0; p < 2; p++) {
                var s;
                try { s = window.getComputedStyle(el, p ? '::after' : '::before'); } catch (e) { continue; }
                if (!s || !s.content || s.content === 'none' || s.content === 'normal') continue;
                if (s.position !== 'absolute' && s.position !== 'fixed') continue;
                var l = parseFloat(s.left), r = parseFloat(s.right), w = parseFloat(s.width);
                var out = (l < -0.5) || (r < -0.5) ||
                    (isFinite(w) && isFinite(l) && l + w > el.clientWidth + 0.5);
                if (out) flags += (flags ? ' ' : '') + (p ? 'a' : 'b');
            }
            if (flags) { el.setAttribute('data-pdf-nodecor', flags); marked++; }
        }
    } catch (e) {}
    return marked;
}

/**
 * Letter-spacing on a joined (cursive) script.
 *
 * With any letter-spacing other than normal html2canvas stops drawing words
 * and draws grapheme by grapheme — which is fine for Latin and Cyrillic and
 * wrecks Arabic: every letter comes out in its isolated form, overlapping its
 * neighbours. The site's h2 carries -0.3px, so on /ar every report heading
 * printed as a heap of letters (measured 16.10.2026, Chromium and WebKit). CSS
 * Text says letter-spacing should not be applied to cursive scripts at all, so
 * on paper it is switched off for elements whose own text is Arabic.
 */
var CURSIVE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
function cursiveSpacingOff(root) {
    try {
        var all = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
        for (var i = 0; i < all.length; i++) {
            var el = all[i], own = '';
            for (var c = el.firstChild; c; c = c.nextSibling) if (c.nodeType === 3) own += c.nodeValue;
            if (!own || !CURSIVE.test(own)) continue;
            var ls = window.getComputedStyle(el).letterSpacing;
            if (ls && ls !== 'normal' && parseFloat(ls) !== 0) el.style.letterSpacing = 'normal';
        }
    } catch (e) {}
}

/**
 * Direction of the report. A connected node answers through its computed
 * style; a detached one has none (it reads ''), so its own `dir`, else the
 * page's, decides — a tool that hands the door a report built off-DOM on an
 * Arabic page made an ltr PDF (21.10.2026, /hand-arm-vibration-calculator).
 */
function reportDirection(el) {
    try {
        if (el.isConnected) return window.getComputedStyle(el).direction;
        var own = el.closest ? el.closest('[dir]') : null;
        var d = (own && own.getAttribute('dir')) || document.documentElement.getAttribute('dir') || document.dir || '';
        return String(d).toLowerCase();
    } catch (e) { return ''; }
}

/**
 * Right-to-left text on html2canvas' canvas.
 *
 * html2canvas lays the copy out correctly (the browser does the bidi), then
 * draws every word with its trailing space or comma by `fillText` at the
 * word's measured LEFT edge — on a detached canvas whose direction is ltr.
 * In an ltr paragraph a trailing space or «،» sits to the RIGHT of an Arabic
 * word, so every word was drawn one space off its slot: «البطاقة1», «على
 * ،الورق», «:المجموع», «(صفحة 3)» → «صفحة) ،(3», and in WebKit the heading's
 * words ran together (measured 16.10.2026 on a report block against the page
 * itself, Chromium and WebKit; tests/playwright/pdf-report-ceiling-glass-fonts.mjs,
 * case E). Drawing each run in the REPORT'S direction (rtl) with
 * `textAlign:'left'` keeps the measured left edge and puts the neutrals where
 * the page put them. Variants measured on the same block (ink mismatch with
 * the page, Chromium / WebKit): as is 0.73 / 0.81 · rtl only for runs with an
 * Arabic letter 0.71 / 0.68 (numbers and Latin words inside Arabic still lose
 * their space) · Arabic rtl, Latin ltr 0.66 / 0.44 · every run in the
 * report's direction 0.63 / 0.37 — chosen.
 *
 * A run with no right-to-left letter («21:», «3).», «+7-999-123.») is drawn
 * rtl too, on purpose. Drawing such runs ltr was measured and rejected
 * (21.10.2026, stand case E): html2canvas already cuts «5349–1» at the dash
 * and places every piece where the page put it, while «21:» and «3).» —
 * the common case, a number with its punctuation in an Arabic sentence —
 * read «:21» and «.(3» on the page, which only the rtl drawing reproduces.
 * What stays wrong is a digits-and-neutrals piece the page laid out ltr
 * because of a Latin word before it, kept whole by html2canvas (a range
 * with a non-breaking hyphen after «ISO»): fillText sees no context, and an
 * ltr rule would reverse the same piece in Arabic context («1–5» reads «5–1»
 * there, as an Arabic reader expects). Context-free, the rtl rule is the
 * smaller error.
 *
 * Only html2canvas' canvas is touched — a detached one, while the export
 * runs — and only text it draws at the start edge (list markers are drawn
 * right-aligned and stay as they are). An ltr report is not touched at all.
 * @returns {function} restore
 */
function rtlTextDrawing(el) {
    try {
        if (reportDirection(el) !== 'rtl') return function () {};
        var P = window.CanvasRenderingContext2D && CanvasRenderingContext2D.prototype;
        if (!P || !('direction' in P)) return function () {};
        var orig = P.fillText;
        P.fillText = function (text, x, y, maxWidth) {
            var c = this.canvas, a = this.textAlign;
            if (!c || c.isConnected || (a !== 'start' && a !== 'left')) {
                return maxWidth === undefined ? orig.call(this, text, x, y) : orig.call(this, text, x, y, maxWidth);
            }
            var d0 = this.direction;
            this.direction = 'rtl';
            this.textAlign = 'left';
            try {
                return maxWidth === undefined ? orig.call(this, text, x, y) : orig.call(this, text, x, y, maxWidth);
            } finally { this.direction = d0; this.textAlign = a; }
        };
        return function () { if (P.fillText !== orig) P.fillText = orig; };
    } catch (e) { return function () {}; }
}

/**
 * Bullets of a right-to-left list.
 *
 * html2canvas draws a list marker right-aligned just LEFT of the item box —
 * for an rtl list that is the far side of the page, a column of dots away
 * from their lines (/ar/inflation-calculator, Chromium and WebKit, 16.10.2026).
 * In html2canvas' copy a bulleted rtl item gets its marker as a real glyph at
 * its start (right) edge instead, where the page shows it. Numbered lists are
 * left as they are: their counters are the browser's to compute.
 */
var RTL_BULLET = { disc: '\u2022', circle: '\u25E6', square: '\u25AA' };
function rtlListMarkers(doc) {
    try {
        var view = doc.defaultView || window, items = doc.querySelectorAll('li'), n = 0;
        for (var i = 0; i < items.length; i++) {
            var li = items[i], cs = view.getComputedStyle(li);
            var glyph = RTL_BULLET[cs.listStyleType];
            if (cs.direction !== 'rtl' || cs.display !== 'list-item' || !glyph || cs.listStylePosition !== 'outside'
                || (cs.listStyleImage && cs.listStyleImage !== 'none')) continue;
            var mark = doc.createElement('span');
            mark.setAttribute('aria-hidden', 'true');
            mark.textContent = glyph;
            mark.style.cssText = 'position:absolute;right:-1.1em;top:0;font:inherit;line-height:inherit;color:inherit';
            if (cs.position === 'static') li.style.position = 'relative';
            li.style.listStyleType = 'none';
            li.insertBefore(mark, li.firstChild);
            n++;
        }
        return n;
    } catch (e) { return 0; }
}

/**
 * Words of a right-to-left report — one element each, on html2canvas' COPY.
 *
 * html2canvas draws a word together with the space after it ("word ") from
 * the left edge of that piece's measured box, and rtlTextDrawing draws it
 * right to left — the space lands on the left, the glyphs to its right.
 * Inside a line the box includes the space, so the glyphs land where the page
 * has them. The LAST word of an rtl line (its left end) is the exception: the
 * browser hangs that space outside the line, the box ends at the glyphs, and
 * the glyphs are drawn one space to the right — onto the next word:
 * «العاملونفي», «المشرفالتحقق» (22.10.2026; a 600 px Arabic paragraph: 8 of
 * 152 neighbour pairs with no gap; /ar/loan-calculator's report: 2; Chromium
 * and WebKit alike). An English paragraph inside the rtl report is drawn
 * right to left too, and there EVERY word moves one space onto its neighbour
 * («check itdaily», «ACME PtyLtd»).
 *
 * With each word in its own element and the spaces left as text between
 * them, no drawn piece carries a space: its box is its glyphs. The layout
 * does not change — the elements are plain inline boxes of an unknown tag
 * (no site rule styles `pdf-w`), and the bidi algorithm sees the same text.
 * A flex or grid parent gets the words inside ONE wrapper, so its items stay
 * as they were.
 *
 * A paragraph that runs LEFT to right inside the rtl report (an English
 * cell of an Arabic table) needs one more thing: drawn right to left, a
 * neutral at a piece's edge changed sides — «gloves.» → «.gloves», and a
 * word broken at its hyphen lost its order («full-» → «-full»). Its words
 * with no Arabic letter carry LRM at the start and after every character
 * that is not a left-to-right letter — the marks /risk-assessment-generator
 * puts in its own report since 22.10.2026 — so each piece keeps its order
 * however html2canvas cuts it. A word of an rtl paragraph is NOT marked: the
 * page lays «21:» out as «:21» there, and the rtl drawing reproduces it
 * (marking by content was measured and rejected 21.10.2026).
 *
 * A word of an rtl paragraph whose characters sit at DIFFERENT bidi levels on
 * the page is cut further, into pieces of one class each — right-to-left
 * letters, left-to-right letters, digits, every other character on its own
 * (combining marks and joiners stay with the character before them). Such a
 * word's glyphs are split across the line: in «… رافعة، in accordance with the
 * manufacturer instructions, 2 workers.» the final «.» belongs to the Arabic
 * paragraph and stands at the LEFT end of the English run, «workers» at its
 * right end; html2canvas measured the word as one box — from the period to
 * the letters — and drew «workers.» at that box's left edge, over
 * «accordance»; «(full-», «harness)» and the «1:» after «ISO 5349–» likewise
 * (22.10.2026; /risk-assessment-generator's work cell, Chromium and WebKit;
 * the «not fixable without context» case of 21.10). A piece of one class is
 * contiguous on the page, so its box is its glyphs and it is drawn where the
 * page put it — order and side come from the page, not from the drawing
 * direction. A mirrored character («(», «»», «≤»…) drawn alone right to left
 * is mirrored by the canvas; where the page resolves it left to right (strong
 * left-to-right text on both sides in the paragraph, digits counting as their
 * preceding strong text — UAX #9 W7/N1) it gets LRM on both sides, which the
 * page already resolves the same way, so the layout does not move.
 */
function bidiRange(a, b) { return String.fromCharCode(a) + '-' + String.fromCharCode(b); }
var BIDI_RTL_LETTER = new RegExp('[' + bidiRange(0x0590, 0x08FF) + bidiRange(0xFB1D, 0xFDFF) + bidiRange(0xFE70, 0xFEFC) + ']');
var BIDI_LTR_LETTER = new RegExp('[A-Za-z' + bidiRange(0x00C0, 0x024F) + bidiRange(0x0370, 0x058F) + bidiRange(0x0900, 0x0DFF)
    + bidiRange(0x0E00, 0x0EFF) + bidiRange(0x10A0, 0x10FF) + bidiRange(0x1100, 0x11FF) + bidiRange(0x1E00, 0x1FFF)
    + bidiRange(0x3040, 0x30FF) + bidiRange(0x3400, 0x9FFF) + bidiRange(0xAC00, 0xD7AF) + ']');
var LRM_MARK = String.fromCharCode(0x200E);
var WORD_SKIP = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, TITLE: 1, OPTION: 1, NOSCRIPT: 1, TEMPLATE: 1 };

function ltrMarkedWord(w) {
    var out = LRM_MARK;
    for (var i = 0; i < w.length; i++) {
        var c = w.charAt(i);
        out += c;
        if (!BIDI_LTR_LETTER.test(c)) out += LRM_MARK;
    }
    return out + (BIDI_LTR_LETTER.test(w.charAt(w.length - 1)) ? LRM_MARK : '');
}

/** The paragraph a text node belongs to — its nearest box that is not plain inline — and its direction. */
function paragraphOf(el, view) {
    for (var e = el; e && e.nodeType === 1; e = e.parentElement) {
        var cs = view.getComputedStyle(e);
        if (cs.display !== 'inline' || (cs.unicodeBidi && cs.unicodeBidi !== 'normal')) return { el: e, dir: cs.direction };
    }
    return { el: null, dir: 'rtl' };
}

// Bidi classes for cutting a word of an rtl paragraph (see the docblock above).
var BIDI_AN = new RegExp('[' + bidiRange(0x0660, 0x0669) + bidiRange(0x06F0, 0x06F9) + ']');
var BIDI_DIGIT = new RegExp('[0-9' + bidiRange(0x00B2, 0x00B3) + String.fromCharCode(0x00B9) + bidiRange(0x2070, 0x2079)
    + bidiRange(0x2080, 0x2089) + bidiRange(0xFF10, 0xFF19) + ']');
var BIDI_JOIN = new RegExp('[' + bidiRange(0x0300, 0x036F) + bidiRange(0x0483, 0x0489) + bidiRange(0x1AB0, 0x1AFF)
    + bidiRange(0x1DC0, 0x1DFF) + bidiRange(0x20D0, 0x20FF) + bidiRange(0xFE00, 0xFE0F) + bidiRange(0xFE20, 0xFE2F)
    + bidiRange(0x200C, 0x200F) + String.fromCharCode(0x061C) + ']');
var BIDI_MIRROR = '()[]{}<>' + String.fromCharCode(0x00AB, 0x00BB, 0x2039, 0x203A, 0x2264, 0x2265);
var ZWJ_CODE = 0x200D;

function bidiClass(c) {
    if (BIDI_JOIN.test(c)) return 'J';
    if (BIDI_AN.test(c)) return 'A';
    if (BIDI_RTL_LETTER.test(c)) return 'R';
    if (BIDI_LTR_LETTER.test(c)) return 'L';
    if (BIDI_DIGIT.test(c)) return 'D';
    return 'N';
}

/** A word cut into pieces of one bidi class; [{t, k, at}] with `at` the offset of the piece in the word. */
function bidiPieces(w) {
    var out = [], i = 0, glue = false;
    while (i < w.length) {
        var code = w.charCodeAt(i), n = (code >= 0xD800 && code <= 0xDBFF && i + 1 < w.length) ? 2 : 1;
        var c = w.substr(i, n), k = n === 2 ? 'N' : bidiClass(c), last = out[out.length - 1];
        if (last && (k === 'J' || glue || (k !== 'N' && last.k === k))) last.t += c;
        else out.push({ t: c, k: k === 'J' ? 'N' : k, at: i });
        glue = code === ZWJ_CODE;
        i += n;
    }
    return out;
}

/** 'L' or 'R': the strong direction a character of the paragraph's text resolves to, '' if it has none, 'D' for a digit. */
function strongOf(c) {
    var code = c.charCodeAt(0);
    if (code === 0x200E) return 'L';
    if (code === 0x200F || code === 0x061C) return 'R';
    var k = bidiClass(c);
    return k === 'L' ? 'L' : (k === 'R' || k === 'A') ? 'R' : (k === 'D' ? 'D' : '');
}

/** How the page resolves a neutral at [pos, pos+len) of an rtl paragraph's text: 'L' only between left-to-right text. */
function neutralSide(text, pos, len) {
    var prev = 'R', next = '', j, s;
    for (j = pos - 1; j >= 0; j--) { s = strongOf(text.charAt(j)); if (s === 'L' || s === 'R') { prev = s; break; } }
    for (j = pos + len; j < text.length; j++) {
        s = strongOf(text.charAt(j));
        if (s === 'D') { next = prev; break; }      // W7: digits take the strong text before them
        if (s === 'L' || s === 'R') { next = s; break; }
    }
    return (next || 'R') === prev ? prev : 'R';
}

function rtlWordSpans(doc) {
    var words = 0;
    try {
        var root = doc.querySelector('.html2pdf__container') || doc.body;
        if (!root) return 0;
        var view = doc.defaultView || window, html = 'http://www.w3.org/1999/xhtml';
        var walker = doc.createTreeWalker(root, 4, null), list = [], n;
        while ((n = walker.nextNode())) list.push(n);
        var paras = typeof Map === 'function' ? new Map() : null;
        // The logical text of each paragraph, for the neighbours of a neutral across element borders.
        var info = [], texts = typeof Map === 'function' ? new Map() : null;
        for (var i = 0; i < list.length; i++) {
            var tn = list[i], pp = tn.parentNode, pg = null;
            if (pp && pp.nodeType === 1) {
                pg = paras && paras.get(pp);
                if (!pg) { pg = paragraphOf(pp, view); if (paras) paras.set(pp, pg); }
            }
            var key = pg && pg.el, start = 0;
            if (key && texts) { start = (texts.get(key) || '').length; texts.set(key, (texts.get(key) || '') + tn.nodeValue); }
            info.push({ pg: pg, start: start });
        }
        for (i = 0; i < list.length; i++) {
            var t = list[i], p = t.parentNode, s = t.nodeValue;
            if (!p || p.nodeType !== 1 || p.namespaceURI !== html || WORD_SKIP[p.nodeName] || !/\S/.test(s)) continue;
            var pinfo = info[i].pg || paragraphOf(p, view), d = pinfo.dir;
            var ptext = (pinfo.el && texts && texts.get(pinfo.el)) || s, base = (pinfo.el && texts) ? info[i].start : 0;
            var parts = s.split(/(\s+)/), ltr = d === 'ltr', marks = false, cut = false;
            for (var k = 0; k < parts.length; k++) {
                if (!parts[k]) continue;
                if (ltr && !BIDI_RTL_LETTER.test(parts[k]) && BIDI_LTR_LETTER.test(parts[k])) marks = true;
                if (!ltr && /\S/.test(parts[k]) && bidiPieces(parts[k]).length > 1) cut = true;
            }
            if (!/\s/.test(s) && !marks && !cut) continue;
            var frag = doc.createDocumentFragment(), off = 0;
            for (k = 0; k < parts.length; k++) {
                var part = parts[k];
                if (!part) continue;
                if (!/\S/.test(part)) { frag.appendChild(doc.createTextNode(part)); off += part.length; continue; }
                var pieces = ltr ? null : bidiPieces(part);
                if (pieces && pieces.length > 1) {
                    for (var q = 0; q < pieces.length; q++) {
                        var pc = pieces[q], pe = doc.createElement('pdf-w'), txt = pc.t;
                        if (pc.k === 'N' && pc.t.length === 1 && BIDI_MIRROR.indexOf(pc.t) >= 0
                            && neutralSide(ptext, base + off + pc.at, 1) === 'L') txt = LRM_MARK + pc.t + LRM_MARK;
                        pe.textContent = txt;
                        frag.appendChild(pe);
                    }
                    words++;
                    off += part.length;
                    continue;
                }
                var w = doc.createElement('pdf-w');
                w.textContent = (ltr && !BIDI_RTL_LETTER.test(part) && BIDI_LTR_LETTER.test(part)) ? ltrMarkedWord(part) : part;
                frag.appendChild(w);
                words++;
                off += part.length;
            }
            if (/flex|grid/.test(view.getComputedStyle(p).display)) {
                var wrap = doc.createElement('pdf-w');
                wrap.appendChild(frag);
                p.replaceChild(wrap, t);
            } else {
                p.replaceChild(frag, t);
            }
        }
    } catch (e) {}
    return words;
}

function openDisclosures(root) {
    var d = root.querySelectorAll('details');
    for (var i = 0; i < d.length; i++) d[i].open = true;
}

/**
 * Scroll boxes and anything still wider than the page.
 *
 * `overflow-x:auto` is a promise that the rest is one swipe away; on paper it
 * is a cut. Un-clip them, then scale down what is still too wide - a table at
 * 80% is readable, a table with three columns missing is not.
 */
function fitWideContent(root, availPx) {
    var all = root.querySelectorAll('*'), i, n, s;
    for (i = 0; i < all.length; i++) {
        n = all[i];
        try { s = window.getComputedStyle(n); } catch (e) { continue; }
        if (s.overflowX === 'auto' || s.overflowX === 'scroll') n.style.overflowX = 'visible';
        if (s.overflowY === 'auto' || s.overflowY === 'scroll') n.style.overflowY = 'visible';
    }

    /* Which node to shrink: walk down while exactly ONE child is too wide, and
     * stop where the overflow first becomes shared. That is the smallest block
     * whose scaling costs nothing else - shrink the card around a table and the
     * whole card loses type size; shrink each <tr> separately (the first cut of
     * this, measured 2026-08-31) and the table comes out with rows at different
     * sizes. Table parts are never a unit of their own: a row scaled alone no
     * longer lines up with its header, so the <table> is the answer for all of
     * them. */
    var INNER_TABLE = { TBODY: 1, THEAD: 1, TFOOT: 1, TR: 1, TD: 1, TH: 1, COLGROUP: 1, COL: 1 };
    var targets = [];

    function resolveTarget(node) {
        if (INNER_TABLE[node.tagName]) {
            var t = node.closest ? node.closest('table') : null;
            if (t && root.contains(t)) return t;
        }
        return node;
    }

    function collect(node) {
        if (node.scrollWidth <= availPx + 1) return;
        var over = [];
        for (var k = 0; k < node.children.length; k++) {
            if (node.children[k].scrollWidth > availPx + 1) over.push(node.children[k]);
        }
        if (over.length === 1) { collect(over[0]); return; }
        var t = resolveTarget(node);
        if (targets.indexOf(t) === -1) targets.push(t);
    }
    for (i = 0; i < root.children.length; i++) collect(root.children[i]);

    for (i = 0; i < targets.length; i++) {
        var el = targets[i];
        var w = el.scrollWidth;
        if (!w) continue;
        var ratio = Math.max(0.4, availPx / w);
        var h = el.offsetHeight;
        /* transform does not shrink the space the element takes in flow, so it
         * gets a box of the scaled height to sit in - otherwise the page keeps
         * a table's worth of blank under every table it shrank. */
        var box = document.createElement('div');
        box.style.cssText = 'overflow:visible;width:100%;height:' + Math.ceil(h * ratio) + 'px;';
        el.parentNode.insertBefore(box, el);
        box.appendChild(el);
        el.style.transformOrigin = 'left top';
        el.style.transform = 'scale(' + ratio + ')';
        el.style.width = w + 'px';
        el.style.maxWidth = 'none';
        el.style.flex = '0 0 auto';
    }
}

/**
 * Inline <svg> that carries a viewBox and no width/height attributes.
 *
 * html2canvas does not rasterise an inline SVG: it re-serialises the element
 * into a `data:image/svg+xml` <img> and then draws
 * `drawImage(img, 0, 0, intrinsicWidth, intrinsicHeight, ...)`, where the
 * intrinsic size comes from the ATTRIBUTES. With none, the <img> gets the
 * replaced-element default (300 px) while `width.baseVal.value` reports
 * something else entirely, so the source rectangle and the image disagree and
 * the drawing comes out zoomed into its own top-left corner. On /synastry the
 * whole bi-wheel became one beige arc across the first page, and every planet
 * glyph in the aspect grid was a blank cell - the top-left ninth of a 100x100
 * viewBox is empty. Sizing the attributes to what the element already measures
 * changes no layout (CSS wins over presentation attributes) and makes the two
 * numbers agree.
 *
 * Only the SIZE. Inlining the computed paint properties as well was tried
 * and reverted the same day: the rendered page came back byte-identical, so
 * the stylesheet's fill/stroke does reach the drawing on its own, and the
 * extra pass was cost and risk buying nothing.
 */
function sizeInlineSvg(root) {
    var svgs = root.querySelectorAll('svg');
    for (var i = 0; i < svgs.length; i++) {
        var el = svgs[i];
        if (el.hasAttribute('width') && el.hasAttribute('height')) continue;
        var r;
        try { r = el.getBoundingClientRect(); } catch (e) { continue; }
        if (!r || r.width < 1 || r.height < 1) continue;
        el.setAttribute('width', Math.round(r.width * 100) / 100);
        el.setAttribute('height', Math.round(r.height * 100) / 100);
    }
}

/**
 * Text a tool paints pale because its own ground is dark.
 *
 * The clone carries the site's LIGHT palette (see createPdfClone), so anything
 * coloured through the theme variables is already right. This catches the rest.
 * Only text over a transparent or near-white ground is touched: a dark badge
 * with white lettering is legible on paper and stays exactly as designed.
 */
function fixInvisibleText(root) {
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
        var n = all[i], s;
        try { s = window.getComputedStyle(n); } catch (e) { continue; }
        if (!isLightColor(s.color)) continue;
        if (!groundIsLight(n)) continue;
        n.style.color = '#1e293b';
    }
}

/** Walk up until something actually paints a background, and judge that one. */
function groundIsLight(el) {
    var n = el;
    while (n && n.nodeType === 1) {
        var s;
        try { s = window.getComputedStyle(n); } catch (e) { return true; }
        var rgb = parseRGB(s.backgroundColor);
        if (rgb && (rgb[3] === undefined || rgb[3] > 0.35)) {
            return (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114) > 140;
        }
        n = n.parentElement;
    }
    return true; // nothing painted - the page ground is white
}

/**
 * A self-contained clone of the element, laid out at the real page width and
 * coloured with the site's own light theme.
 */
function createPdfClone(el, title, opts) {
    var innerMm = pageInnerMm(opts);
    var availPx = innerMm * MM_PX;

    var wrapper = document.createElement('div');
    /* `data-theme=light` rather than a luminance walk over every node.
     *
     * The walk that used to live here read `getComputedStyle` off a DETACHED
     * clone, which returns an empty declaration in every engine - so it had
     * been a no-op since the day it was written, and a dark-theme reader got a
     * white PDF with #94a3b8 body text on it. The site already ships a light
     * palette behind this one attribute; using it is both correct and visible
     * to whoever designs the page.
     *
     * The font is deliberately NOT overridden. Forcing
     * `Inter,system-ui,sans-serif` here measured the text in the page's own
     * stack and drew it in a different one, and the ~5% width difference ate
     * the space after every word longer than three letters:
     * "SHkalapokazyvaetbalans...".
     */
    wrapper.setAttribute('data-theme', 'light');
    wrapper.style.cssText = 'color-scheme:light;color:#1e293b;background:#ffffff;' +
        'box-sizing:border-box;width:100%;padding:24px;margin:0;';

    // Header with branding
    if (title) {
        var header = document.createElement('div');
        header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #e2e8f0;padding-bottom:12px;margin-bottom:20px;';
        header.innerHTML = '<div style="display:flex;align-items:center;gap:8px;">' +
            '<svg width="28" height="28" viewBox="0 0 512 512" fill="#2563eb"><circle cx="256" cy="256" r="240" fill="#2563eb"/><path d="M180 160c0-12 8-20 20-20h8c12 0 20 8 20 20v100h56V160c0-12 8-20 20-20h8c12 0 20 8 20 20v192c0 12-8 20-20 20h-8c-12 0-20-8-20-20v-60h-56v60c0 12-8 20-20 20h-8c-12 0-20-8-20-20V160z" fill="#fff"/></svg>' +
            '<span style="font-size:18px;font-weight:700;color:#1e293b;">Timbrica</span></div>' +
            '<div style="font-size:12px;color:#64748b;">' + new Date().toLocaleDateString() + '</div>';
        wrapper.appendChild(header);

        var titleEl = document.createElement('h2');
        titleEl.style.cssText = 'font-size:20px;font-weight:700;color:#1e293b;margin:0 0 16px;';
        titleEl.textContent = title;
        wrapper.appendChild(titleEl);
    }

    // Clone the content
    /* The id is KEPT. A duplicate id is harmless here - the measuring host is
     * appended after the original, so getElementById still answers with the
     * live one - while dropping it would silently kill every `#report .row`
     * rule a tool happens to have written. */
    var clone = el.cloneNode(true);

    // cloneNode(true) does NOT copy canvas bitmaps - every painted <canvas>
    // would render blank (the dead-pixel-test minimap bug). Copy the pixels.
    var srcCanvases = el.querySelectorAll('canvas');
    var dstCanvases = clone.querySelectorAll('canvas');
    for (var i = 0; i < srcCanvases.length && i < dstCanvases.length; i++) {
        try {
            var src = srcCanvases[i], dst = dstCanvases[i];
            dst.width = src.width; dst.height = src.height;
            if (src.width > 0 && src.height > 0) {
                dst.getContext('2d').drawImage(src, 0, 0);
            }
        } catch (e) { /* tainted canvas - leave blank rather than abort */ }
    }

    wrapper.appendChild(clone);

    // Footer
    var footer = document.createElement('div');
    footer.style.cssText = 'margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:12px;color:#94a3b8;text-align:center;';
    footer.textContent = commonString('pdf_generated_by') || 'Generated by Timbrica - timbrica.com';
    wrapper.appendChild(footer);

    /* Everything below needs real layout, and layout needs the document. The
     * host is exactly one page wide, so what we measure is what html2pdf will
     * lay out; it is parked off-screen rather than hidden, because
     * `display:none` has no geometry either. */
    var host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:fixed;left:-100000px;top:0;width:' + innerMm + 'mm;' +
        'pointer-events:none;z-index:-1;';
    var mount = host;
    var chain = containerChain(el);
    for (var c = 0; c < chain.length; c++) {
        var qc = document.createElement('div');
        qc.style.cssText = 'width:100%;container-type:' + chain[c].type + ';' +
            (chain[c].name ? 'container-name:' + chain[c].name + ';' : '');
        mount.appendChild(qc);
        mount = qc;
    }
    mount.appendChild(wrapper);
    document.body.appendChild(host);
    try {
        openDisclosures(wrapper);
        /* The zero-bitmap floor, for the COPY. renderAndDeliver applies it to
           what it is handed — and on this, the default path, that is the copy
           AFTER it was detached again: no geometry, every canvas looks
           "invisible", the floor is skipped, and the 0×0 canvas the copy
           inherited (dst.width = src.width) throws InvalidStateError inside
           html2canvas. Measured 16.10.2026: a report with one such canvas
           failed with code 11 in Chromium and WebKit, old and new generator
           alike; /depression-test passed only because its own panel is drawn
           in live mode. Here the copy is attached, and it is ours to keep 1×1. */
        neutraliseZeroCanvases(wrapper);
        cursiveSpacingOff(wrapper);
        if (dropOverhangingDecor(wrapper)) {
            var decorOff = document.createElement('style');
            decorOff.textContent = OVERHANG_STYLE;
            wrapper.insertBefore(decorOff, wrapper.firstChild);
        }
        fitWideContent(wrapper, availPx - 48 /* the wrapper's own padding */);
        /* after fitWideContent: it is the same getBoundingClientRect html2canvas
         * will read, so a block that got scaled stays consistent with it. */
        sizeInlineSvg(wrapper);
        fixInvisibleText(wrapper);
        /* ⚠️⚠️ ЗДЕСЬ, а не у съёмки: ниже клон ОТЦЕПЛЯЕТСЯ от документа, а у
           отцепленного узла `getComputedStyle` возвращает пустую декларацию —
           обход по нему не находит НИЧЕГО и выглядит сработавшим. Я сам на
           это попался: правка молча ничего не делала, и доказательством
           «починки» служил соседний тул, у которого цвет убран в самом CSS. */
        neutraliseModernColors(wrapper);
    } catch (e) {
        try { if (window.console) console.warn('PDF layout pass failed:', e); } catch (e2) {}
    }
    /* Hand html2pdf the OUTERMOST node, so the query-container context travels
     * with the clone; the host itself is position:fixed and must not. */
    var out = chain.length ? host.firstChild : wrapper;
    try { host.removeChild(out); } catch (e) {}
    try { document.body.removeChild(host); } catch (e) {}
    return out;
}

function isLightColor(c) {
    var rgb = parseRGB(c);
    if (!rgb) return false;
    if (rgb[3] !== undefined && rgb[3] < 0.35) return false;
    return (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114) > 165;
}

function parseRGB(c) {
    if (!c) return null;
    var m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (m) return [+m[1], +m[2], +m[3], m[4] !== undefined ? +m[4] : 1];
    return null;
}

/**
 * Один пустой холст не имеет права уносить ВЕСЬ отчёт.
 *
 * Холст, у которого битмап 0, а коробка в раскладке живая, — обычное следствие
 * того, что тул рисовал его в скрытой панели (`getBoundingClientRect()` там
 * нули). На экране это просто пустое место, а вот html2canvas зовёт по нему
 * `drawImage` и получает `InvalidStateError: … canvas element with a width or
 * height of 0` — и отчёт не выходит вовсе.
 *
 * Замер, из-за которого это здесь (10.09.2026, /depression-test, 90 дней):
 * 94 отказа PDF против 12 успехов; разделителем оказался не браузер и не
 * устройство, а режим прохождения — 20 из 21 сессии с отказом шли в
 * «Расширенном», где рисуется радар доменов, единственный такой холст. Люди
 * жали кнопку до 22 раз подряд и не получали ничего.
 *
 * Тул свою причину чинит у себя (там холст обязан быть настоящим), а здесь —
 * пол для всех 43 потребителей: пустой холст на время съёмки становится 1x1 и
 * возвращается как был. Клон-режим уже не копировал в него пиксели
 * (`src.width > 0`), но САМ нулевой размер оставался и в клоне.
 *
 * ⚠️ Проверять это подсунутым `<canvas width=0 height=0>` БЕСПОЛЕЗНО: без
 * раскладки html2canvas такой холст пропускает молча, и опыт «доказывает»
 * несуществующую устойчивость (я так и ошибся в первом заходе). Настоящее
 * условие — нулевой битмап ПРИ ненулевой коробке.
 *
 * @returns {function} восстановление; идемпотентно.
 */
function neutraliseZeroCanvases(root) {
    var touched = [];
    try {
        var list = root && root.querySelectorAll ? root.querySelectorAll('canvas') : [];
        for (var i = 0; i < list.length; i++) {
            var c = list[i];
            if (c.width > 0 && c.height > 0) continue;
            var r;
            try { r = c.getBoundingClientRect(); } catch (e) { r = null; }
            var laidOut = (r && (r.width >= 1 || r.height >= 1)) || c.offsetWidth >= 1 || c.offsetHeight >= 1;
            if (!laidOut) continue;   // невидимый и пустой — html2canvas его и так пропустит
            touched.push([c, c.width, c.height]);
            c.width = 1;
            c.height = 1;
        }
    } catch (e) { /* защита не имеет права стать причиной отказа */ }
    var done = false;
    return function() {
        if (done) return;
        done = true;
        for (var j = 0; j < touched.length; j++) {
            try { touched[j][0].width = touched[j][1]; touched[j][0].height = touched[j][2]; } catch (e) {}
        }
    };
}

/* Свойства, в значении которых может оказаться цвет. `backgroundImage` здесь
   не лишний: градиенты тоже несут цвета, и один `oklch` в градиенте роняет
   съёмку так же, как в тексте. */
var COLOR_PROPS = ['color', 'background-color', 'background-image', 'border-top-color',
    'border-right-color', 'border-bottom-color', 'border-left-color', 'outline-color',
    'text-shadow', 'box-shadow', 'text-decoration-color', 'caret-color',
    'column-rule-color', 'fill', 'stroke', 'accent-color'];

/* Формы, которых html2canvas не знает. `color-mix` сюда не нужен — браузер
   отдаёт его уже вычисленным в `color(...)`. */
var MODERN_COLOR = /\b(?:color|oklab|oklch|lab|lch|hwb)\([^()]*\)/g;

/* Универсальный переводчик: цвет рисуется на холсте 1×1 и читается пикселем,
   поэтому понимает ровно то же, что и браузер, без таблиц и формул.
   Результаты кэшируются — на одной странице одинаковых цветов много. */
var _colorCache = null, _colorCtx = null;
function toRgba(value) {
    if (_colorCache && _colorCache[value] !== undefined) return _colorCache[value];
    if (!_colorCache) _colorCache = {};
    var out = null;
    try {
        if (!_colorCtx) {
            var cv = document.createElement('canvas');
            cv.width = cv.height = 1;
            _colorCtx = cv.getContext('2d', { willReadFrequently: true });
        }
        _colorCtx.clearRect(0, 0, 1, 1);
        _colorCtx.fillStyle = value;
        _colorCtx.fillRect(0, 0, 1, 1);
        var d = _colorCtx.getImageData(0, 0, 1, 1).data;
        out = 'rgba(' + d[0] + ', ' + d[1] + ', ' + d[2] + ', ' + (d[3] / 255).toFixed(3) + ')';
    } catch (e) { out = null; }
    _colorCache[value] = out;
    return out;
}

/**
 * Перевести цвета, которых движок съёмки не понимает, в rgba() — и вернуть
 * функцию, возвращающую всё как было.
 *
 * Иначе ОДИН такой цвет где угодно в поддереве отменяет отчёт целиком, а
 * человек читает «PDF generation failed» и не может ни понять, ни обойти.
 */
function neutraliseModernColors(root) {
    var touched = [];
    try {
        if (!root || !root.querySelectorAll) return function () {};
        var nodes = [root];
        var all = root.querySelectorAll('*');
        for (var i = 0; i < all.length; i++) nodes.push(all[i]);
        for (var n = 0; n < nodes.length; n++) {
            var el = nodes[n], cs;
            try { cs = getComputedStyle(el); } catch (e) { continue; }
            for (var k = 0; k < COLOR_PROPS.length; k++) {
                var prop = COLOR_PROPS[k];
                var v = cs.getPropertyValue(prop);
                if (!v || v.indexOf('(') < 0) continue;
                MODERN_COLOR.lastIndex = 0;
                if (!MODERN_COLOR.test(v)) continue;
                MODERN_COLOR.lastIndex = 0;
                var ok = true;
                var fixed = v.replace(MODERN_COLOR, function (m) {
                    var r = toRgba(m);
                    if (!r) { ok = false; return m; }
                    return r;
                });
                if (!ok || fixed === v) continue;
                touched.push([el, prop, el.style.getPropertyValue(prop), el.style.getPropertyPriority(prop)]);
                el.style.setProperty(prop, fixed, 'important');
            }
        }
    } catch (e) { /* защита не имеет права стать причиной отказа */ }
    var done = false;
    return function () {
        if (done) return;
        done = true;
        for (var j = 0; j < touched.length; j++) {
            try {
                var t = touched[j];
                if (t[2]) t[0].style.setProperty(t[1], t[2], t[3]);
                else t[0].style.removeProperty(t[1]);
            } catch (e) {}
        }
    };
}

/**
 * Shared render pipeline: probe -> html2canvas (scroll-safe) -> blank check
 * -> jsPDF blob -> __downloadFile. Rejects with .code-tagged Error.
 */
/**
 * html2canvas `ignoreElements`: what of the page its document copy may skip.
 *
 * What is printed is html2pdf's own copy of the element, in the
 * `.html2pdf__overlay` it appends to <body> — in live mode too. Yet html2canvas
 * first copies the WHOLE document into its iframe: on a real page that is the
 * sidebar with hundreds of links, the footer, every table. A/B on one warm
 * page, 02.10.2026 (/inflation-calculator, 3 runs each): Chromium 11–16 s →
 * 0.7 s, WebKit 11–24 s → 3.4 s.
 *
 * Kept: <head>; the overlay and everything in it; every <style> and
 * stylesheet <link> in <body> with the bare path of elements down to it — the
 * tool console and glass styles are pushed inside the content, and dropping
 * them turned the answer grey in the PDF (the first version of this rule);
 * <svg> sprites whole, for a <use> inside the report. With that the PDFs are
 * pixel-identical (/debt-indexation 4 pages, /inflation-calculator 2 pages).
 */
function pageBranchesNotPrinted() {
    var keepPath = null, keepWhole = null;
    return function(node) {
        var body = document.body;
        if (!body || node === body || !body.contains(node)) return false;
        if (!keepPath) {
            keepPath = new Set();
            keepWhole = [];
            var a = body.querySelectorAll('style, link[rel~="stylesheet"], .html2pdf__overlay, svg symbol');
            for (var i = 0; i < a.length; i++) {
                var el = a[i].tagName.toLowerCase() === 'symbol' ? a[i].closest('svg') : a[i];
                if (!el) continue;
                if (!/^(style|link)$/i.test(el.tagName) && keepWhole.indexOf(el) < 0) keepWhole.push(el);
                for (var n = el; n && n !== body; n = n.parentNode) {
                    if (keepPath.has(n)) break;
                    keepPath.add(n);
                }
            }
        }
        if (keepPath.has(node)) return false;
        for (var j = 0; j < keepWhole.length; j++) if (keepWhole[j].contains(node)) return false;
        return true;
    };
}

/* -- fonts the page and its copy really draw with ---------------------------
 *
 * html2canvas lays the text out in its COPY of the document (an iframe) and
 * draws it on a canvas that belongs to THIS document, with `ctx.font` set to
 * the copy's computed family. Both resolve "Inter" on their own — and they can
 * answer differently.
 *
 * Inter and Noto Sans Arabic are `font-display: optional` (critical.css): a
 * face that misses the first ~100 ms is not used for the rest of the page's
 * life, even after it arrives. The copy is a new document that finds the file
 * already in the cache, so it gets Inter while the page — and its canvas —
 * stay on "Inter Fallback", 3 % wider. Every word is then drawn wider than the
 * slot the copy measured for it: «Обычныйабзац», «Карточка 1 /21».
 * Measured 16.10.2026 on /ru/loan-calculator: the page was on the fallback in
 * 6 of 6 loads after 6 s (and on timbrica.com itself 3 of 3); every PDF of
 * those loads came out with eaten spaces. Not a dev artefact: the main
 * document missing the optional window is the ordinary cold mobile visit.
 *
 * The fix makes the copy agree with the PAGE, in the copy only — the page's
 * canvas is what draws, so the page decides. A family is "failed" on this page
 * when its faces for a sample script are LOADED (FontFace.status, unicode-range
 * covers the sample) and yet the page does not draw the sample with it (canvas
 * probe: the width with a monospace and with a serif fallback behind the
 * family agree only when the family drew every glyph). Such a family is taken
 * out of every font stack of the printed subtree in the copy, so layout and
 * drawing both use what the page itself shows. The decision never reads the
 * copy: whether the copy has the file yet is a race (it asks the cache again,
 * after `fonts.ready` resolved on an empty set), and a decision taken in that
 * race was right in one run and wrong in the next. The page is not touched.
 */
var FONT_SAMPLES = ['Hamburgefonstiv 0123', 'Шкала показывает баланс', 'مقياس التوازن'];

/** Families whose @font-face is `font-display: optional` — the ones that can fail for good. */
function optionalFontFamilies() {
    var out = [];
    try {
        var sheets = document.styleSheets;
        for (var i = 0; i < sheets.length; i++) {
            var rules = null;
            try { rules = sheets[i].cssRules; } catch (e) { continue; }   // чужой origin
            if (!rules) continue;
            for (var j = 0; j < rules.length; j++) {
                var r = rules[j];
                if (!r || r.type !== 5 || !r.style) continue;              // CSSRule.FONT_FACE_RULE
                if (String(r.style.getPropertyValue('font-display')).trim() !== 'optional') continue;
                var fam = String(r.style.getPropertyValue('font-family')).replace(/^\s*["']|["']\s*$/g, '').trim();
                if (fam && out.indexOf(fam) < 0) out.push(fam);
            }
        }
    } catch (e) {}
    return out;
}

/* "U+0400-045F, U+2116, U+4??" → [[lo, hi], …] */
function unicodeRanges(ur) {
    var out = [], parts = String(ur || 'U+0-10FFFF').split(',');
    for (var i = 0; i < parts.length; i++) {
        var m = parts[i].trim().match(/^U\+([0-9A-F?]+)(?:-([0-9A-F]+))?$/i);
        if (!m) continue;
        var lo = parseInt(m[1].replace(/\?/g, '0'), 16);
        var hi = m[2] ? parseInt(m[2], 16) : parseInt(m[1].replace(/\?/g, 'F'), 16);
        out.push([lo, hi]);
    }
    return out;
}

/** Does the page draw `text` in `family`? Two fallbacks behind it agree only when it does. */
function pageDraws(ctx, family, text) {
    ctx.font = '40px "' + family + '", monospace';
    var a = ctx.measureText(text).width;
    ctx.font = '40px "' + family + '", serif';
    return Math.abs(a - ctx.measureText(text).width) < 0.5;
}

/**
 * Can this canvas draw the family's BOLD? Inter and Noto Sans Arabic are variable fonts (wght 400–700). WebKit for
 * Windows (Playwright's) draws weight 700 with the regular outlines on the page AND on a canvas (ink 1511 = 1511 for a
 * sample line, Chromium 1123 → 1591; 16.10.2026), so every <b> in the PDF came out regular. A family whose canvas bold
 * is no bolder than its regular is taken out of the copy like a dropped one: the fallback (Arial) has a real bold.
 * An engine that draws the weight is not touched; whether a phone does it is in `pdf_export.nobold`.
 */
function canvasBolds(family, text) {
    try {
        var c = document.createElement('canvas'); c.width = 640; c.height = 64;
        var x = c.getContext('2d');
        function ink(w) {
            x.clearRect(0, 0, 640, 64);
            x.font = w + ' 40px "' + family + '", monospace';
            x.fillStyle = '#000'; x.textBaseline = 'top';
            x.fillText(text, 2, 8);
            var d = x.getImageData(0, 0, 640, 64).data, n = 0;
            for (var i = 3; i < d.length; i += 4) n += d[i];
            return n;
        }
        var a = ink(400), b = ink(700);
        return !(a > 0) || b > a * 1.08;
    } catch (e) { return true; }
}

/** Does the report print any bold text? (computed weight, own text only; capped walk) */
function usesBold(el) {
    try {
        var all = [el].concat(Array.prototype.slice.call(el.querySelectorAll('*'), 0, 3000));
        for (var i = 0; i < all.length; i++) {
            var own = false;
            for (var c = all[i].firstChild; c; c = c.nextSibling) if (c.nodeType === 3 && /\S/.test(c.nodeValue)) { own = true; break; }
            if (own && parseInt(window.getComputedStyle(all[i]).fontWeight, 10) >= 600) return true;
        }
    } catch (e) {}
    return false;
}

/** Optional families the page has LOADED for some script and still does not draw (or cannot draw bold, when the
    report needs bold — `needBold`; such families are listed in `out.noBold` too). */
function familiesThePageDropped(families, needBold) {
    var out = [];
    out.noBold = [];
    try {
        if (!families.length || !document.fonts) return out;
        var faces = [];
        document.fonts.forEach(function(f) { faces.push(f); });
        var ctx = document.createElement('canvas').getContext('2d');
        for (var i = 0; i < families.length; i++) {
            var ranges = [];
            for (var k = 0; k < faces.length; k++) {
                var fam = String(faces[k].family).replace(/^["']|["']$/g, '');
                if (fam === families[i] && faces[k].status === 'loaded') ranges = ranges.concat(unicodeRanges(faces[k].unicodeRange));
            }
            if (!ranges.length) continue;
            for (var s = 0; s < FONT_SAMPLES.length; s++) {
                var text = FONT_SAMPLES[s].replace(/\s+/g, ''), covered = true;
                for (var c = 0; c < text.length && covered; c++) {
                    var cp = text.charCodeAt(c), hit = false;
                    for (var r = 0; r < ranges.length; r++) if (cp >= ranges[r][0] && cp <= ranges[r][1]) { hit = true; break; }
                    covered = hit;
                }
                if (!covered) continue;
                if (!pageDraws(ctx, families[i], FONT_SAMPLES[s])) { out.push(families[i]); break; }
                if (needBold && !canvasBolds(families[i], FONT_SAMPLES[s])) { out.push(families[i]); out.noBold.push(families[i]); break; }
            }
        }
    } catch (e) {}
    return out;
}

function withoutFamilies(stack, families) {
    var parts = String(stack || '').split(','), keep = [];
    for (var i = 0; i < parts.length; i++) {
        var name = parts[i].replace(/^\s*["']?|["']?\s*$/g, '');
        if (name && families.indexOf(name) < 0) keep.push(parts[i].trim());
    }
    return keep.length ? keep.join(', ') : 'sans-serif';
}

/**
 * In html2canvas' copy: take the families the page dropped out of the printed
 * subtree's font stacks. @returns {boolean} whether anything was changed.
 */
function alignCopyFonts(doc, dropped) {
    try {
        if (!dropped || !dropped.length) return false;
        var root = doc.querySelector('.html2pdf__container') || doc.body;
        var view = doc.defaultView || window;
        var nodes = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
        var changed = false;
        for (var i = 0; i < nodes.length; i++) {
            var cs;
            try { cs = view.getComputedStyle(nodes[i]); } catch (e) { continue; }
            var fam = cs && cs.fontFamily;
            if (!fam) continue;
            var next = withoutFamilies(fam, dropped);
            if (next !== fam) { nodes[i].style.setProperty('font-family', next, 'important'); changed = true; }
        }
        return changed;
    } catch (e) { return false; }
}

/* -- the canvas ceiling ------------------------------------------------------
 *
 * html2pdf paints the whole document onto ONE canvas and cuts the pages out of
 * it. Past the device ceiling that canvas comes back empty without an error —
 * on iOS ≤ 17 that is 16.7 Mpx, i.e. ~5.5 A4 pages at the default scale 2
 * (1438 px wide). Field, 90 days to 16.10.2026: 41 `blank_render` failures,
 * all but two from iOS 15.3–17.6, on english-level-test, personality-test and
 * synastry — the reports that run 6–14 pages; none from iOS 18.
 *
 * So a long report is painted in SLICES of whole pages, each under the ceiling
 * the device has (`canvasLimits()` — the one place that number lives), and
 * every slice is added to the same PDF. Full resolution at any length. A
 * report that fits — nearly all of them — takes exactly the old single pass.
 * A slice the device refuses anyway (memory pressure: the ceiling is a guess
 * about the device, a refusal is a fact) reads back fully transparent —
 * html2canvas always paints an opaque white ground first — and the plan is
 * halved and run again.
 */
/* The table is fetched as soon as an export starts, alongside the library, and
   kept once it arrives; an export that cannot wait for it plans without it and
   leans on the refusal check instead. */
var _limits = null, _limitsLoad = null;
function loadCanvasLimits() {
    if (_limits) return Promise.resolve(_limits);
    if (_limitsLoad) return _limitsLoad;
    _limitsLoad = new Promise(function(resolve) {
        try {
            var src = typeof window.__jsSrc === 'function' ? window.__jsSrc('/js/canvas-limits.js') : '/js/canvas-limits.js';
            import(src).then(function(m) {
                try { _limits = m && typeof m.canvasLimits === 'function' ? m.canvasLimits() : null; } catch (e) {}
                resolve(_limits);
            }, function() { _limitsLoad = null; resolve(null); });
        } catch (e) { _limitsLoad = null; resolve(null); }
    });
    return _limitsLoad;
}
function deviceCanvasLimits() {
    return Promise.race([loadCanvasLimits(), new Promise(function(r) { setTimeout(function() { r(_limits); }, 8000); })]);
}

/** Pages per slice that fit `lim` at this geometry; 0 — not even one page fits. */
function planSlices(geo, scale, lim, cap) {
    var cw = Math.floor(geo.w * scale);
    var total = Math.floor(geo.h * scale);
    var pagePx = Math.max(1, Math.floor(cw * geo.ratio));
    var pages = Math.max(1, Math.ceil(total / pagePx));
    var per = pages;
    if (lim && lim.area && lim.side) {
        var rows = Math.min(lim.side, Math.floor(lim.area / Math.max(1, cw)));
        per = cw > lim.side ? 0 : Math.floor(rows / pagePx);
    }
    if (cap) per = Math.min(per, cap);
    return { cw: cw, total: total, pagePx: pagePx, pages: pages, per: Math.min(per, pages) };
}

/** A canvas the device never gave a backing store reads back fully transparent. */
function canvasRefused(c) {
    try {
        if (!c || !c.width || !c.height) return true;
        var x = c.getContext('2d');
        return x.getImageData(0, 0, 1, 1).data[3] === 0 &&
            x.getImageData(c.width - 1, c.height - 1, 1, 1).data[3] === 0;
    } catch (e) { return false; }
}

function renderAndDeliver(el, opts) {
    var t0 = Date.now();
    var filename = opts.filename || 'timbrica-report.pdf';

    if (canvasReadbackPoisoned()) {
        return Promise.reject(fail('canvas_blocked', 'canvas readback poisoned'));
    }

    var unzero = neutraliseZeroCanvases(el);
    var uncolor = neutraliseModernColors(el);
    var droppedFonts = familiesThePageDropped(optionalFontFamilies(),
        usesBold(opts.element && opts.element.isConnected ? opts.element : el));
    /* After the font probes (their canvas text is not the report's). The SOURCE element decides: on the default
       path `el` is the copy, detached again, and a detached node's computed style is empty — its direction would
       read '' and the fix would silently never run. */
    var unrtl = rtlTextDrawing(opts.element && opts.element.isConnected ? opts.element : el);
    var rtlReport = reportDirection(opts.element && opts.element.isConnected ? opts.element : el) === 'rtl';
    var scale = opts.scale || 2;
    var info = { slices: 1, refused: 0, fonts: 0, pages: 0, scale: scale, nobold: droppedFonts.noBold.length ? 1 : 0 };
    var userOnclone = opts.onclone;

    /* One html2pdf worker per canvas. Each gets its OWN ignoreElements: the
     * rule remembers the overlay it saw first, and every worker brings a new one. */
    function workerFor(h2cExtra, extra) {
        var h2c = {
            scale: scale,
            backgroundColor: '#ffffff',
            useCORS: true,
            // The blank-PDF class fix: html2canvas defaults scrollX/scrollY to the
            // window scroll and shifts the whole capture by it. Ground-truth
            // verified (2026-06-10, chromium+firefox): {0,0} is pixel-identical
            // to an unscrolled capture; the default at scrollY~950 loses ~80%.
            scrollX: 0,
            scrollY: 0,
            /* MUST stay the real viewport width.
             *
             * It is tempting to pass the page width instead, so @media rules are
             * evaluated against the 190 mm the content is actually poured into.
             * Measured 2026-08-31: it shifts the whole capture left by
             * (viewport - pageWidth)/2 and loses that much off the left edge.
             * html2canvas resizes its CLONE iframe to `windowWidth` but takes the
             * capture rectangle from the ORIGINAL document, where html2pdf's
             * container is centred in a viewport-wide overlay; make the two widths
             * disagree and the rectangle no longer points at the content. */
            windowWidth: document.documentElement.clientWidth,
            /* The copy is a new document: every CSS animation in it starts again from
             * its first frame, and html2canvas paints whatever frame it is on. A slow
             * copy hid that (the 0.4 s answer flash had ended by the time a 7 s copy
             * was painted); a fast one printed the flash's grey start. The PDF shows
             * the settled page: no animations, no transitions. */
            onclone: function(doc) {
                try {
                    var st = doc.createElement('style');
                    st.textContent = '*,*::before,*::after{animation:none!important;transition:none!important}';
                    (doc.head || doc.documentElement).appendChild(st);
                } catch (e) {}
                if (alignCopyFonts(doc, droppedFonts)) info.fonts = 1;
                rtlListMarkers(doc);
                if (rtlReport) rtlWordSpans(doc);
                if (userOnclone) return userOnclone.apply(this, arguments);
            },
            ignoreElements: pageBranchesNotPrinted(),
        };
        if (h2cExtra) for (var k in h2cExtra) h2c[k] = h2cExtra[k];
        var o = {
            margin: opts.margin || [10, 10, 10, 10],
            filename: filename,
            image: { type: 'jpeg', quality: 0.95 },
            html2canvas: h2c,
            /* `avoid-all` is right for a report — keep a card whole rather than
             * split it across pages. A caller that paginates deliberately (the
             * document writer breaks on .dw-page-break) passes its own. */
            pagebreak: opts.pagebreak || { mode: ['avoid-all', 'css', 'legacy'] },
            // `format` is an option because a report is not always A4: the watch
            // size sheet is A5. Everything else keeps the default.
            jsPDF: { unit: 'mm', format: opts.format || 'a4', orientation: opts.orientation || 'portrait' },
        };
        if (extra) for (var e in extra) o[e] = extra[e];
        return window.html2pdf().set(o).from(el);
    }

    var MIN_SCALE = 0.75;
    var QUALITY_FLOOR = 1.5;
    var REFUSAL = 'canvas_refused';

    /* The common case: the path this module always took, one canvas. */
    function single(w) {
        return Promise.resolve(w.toCanvas().then(function() {
            var c = this.prop.canvas;
            if (canvasRefused(c)) throw fail(REFUSAL, 'device refused a ' + (c ? c.width + 'x' + c.height : '?') + ' canvas');
            if (canvasLooksBlank(c)) throw fail('blank_render', 'rendered canvas is blank');
        }).toPdf().outputPdf('blob'));
    }

    /* Slices of `plan.per` whole pages, appended to one jsPDF. A slice is laid
     * out by html2pdf exactly like the whole (same container, same page-break
     * padding), and html2canvas paints only its band: `y` is absolute in this
     * html2canvas, and `+ 0.5` px keeps floor() from losing the last row of a
     * band, which would leave a 1 px page behind. */
    function sliced(geo, plan) {
        var S = Math.ceil(plan.pages / plan.per);
        info.slices = S;
        var pdf = null, inked = false, chain = Promise.resolve();
        function band(k) {
            var firstPage = k * plan.per;
            var rows = k === S - 1 ? plan.total - firstPage * plan.pagePx : plan.per * plan.pagePx;
            var w = workerFor(
                { y: geo.top + firstPage * plan.pagePx / scale, height: (rows + 0.5) / scale },
                /* Links are placed once, by the last band, when every page exists:
                   html2pdf positions them over the whole container. */
                pdf ? { pdf: pdf, enableLinks: k === S - 1 } : { enableLinks: k === S - 1 }
            );
            return Promise.resolve(w.toCanvas().then(function() {
                var c = this.prop.canvas;
                if (canvasRefused(c)) throw fail(REFUSAL, 'device refused band ' + k + ' (' + (c ? c.width + 'x' + c.height : '?') + ')');
                if (!canvasLooksBlank(c)) inked = true;
                /* html2pdf draws a band's first page onto the CURRENT page. */
                if (this.prop.pdf) this.prop.pdf.addPage();
            }).toPdf().then(function() {
                pdf = this.prop.pdf;
                if (k === S - 1) {
                    if (!inked) throw fail('blank_render', 'rendered canvas is blank');
                    return this.outputPdf('blob');
                }
            }));
        }
        for (var k = 0; k < S; k++) (function(k) { chain = chain.then(function() { return band(k); }); })(k);
        return chain;
    }

    function measure() {
        var w = workerFor();
        return Promise.resolve(w.toContainer().then(function() {
            var c = this.prop.container, ps = this.prop.pageSize, r = c.getBoundingClientRect();
            return { worker: w, overlay: this.prop.overlay,
                geo: { top: r.top, w: Math.ceil(r.width), h: Math.ceil(r.height), ratio: ps.inner.ratio } };
        }));
    }

    function dropOverlay(m) {
        try { if (m && m.overlay && m.overlay.parentNode) m.overlay.parentNode.removeChild(m.overlay); } catch (e) {}
    }

    function attempt(lim, cap, tries) {
        return measure().then(function(m) {
            var plan = planSlices(m.geo, scale, lim, cap);
            if (plan.per < 1) {
                dropOverlay(m);
                if (scale <= MIN_SCALE) throw fail('blank_render', 'a single page does not fit the canvas ceiling');
                scale = Math.max(MIN_SCALE, scale * 0.75);
                info.scale = scale;
                return attempt(lim, cap, tries + 1);
            }
            info.pages = plan.pages;
            var run;
            /* Too big for one canvas: first give up a little resolution — the
             * largest scale at which ONE canvas still fits, but not below
             * QUALITY_FLOOR — and only past that paint in bands. Measured on
             * the iPhone ceiling (16.10.2026, WebKit, 16-page report): bands at
             * scale 2 took 16.7–19.1 s, a plain scale cap 3.1–3.6 s but at
             * 0.59× the width in pixels, this 6.1–7.8 s at 1.5 (144 dpi). Each
             * band costs a whole html2canvas pass, so fewer bands is the time. */
            if (plan.per < plan.pages && lim && !cap) {
                var fit = Math.min(Math.sqrt(lim.area / (m.geo.w * m.geo.h)),
                    (lim.side - 2) / m.geo.h, (lim.side - 2) / m.geo.w) * 0.995;
                var floorScale = Math.min(scale, QUALITY_FLOOR);
                var next = fit >= floorScale ? fit : floorScale;
                if (next < scale) {
                    dropOverlay(m);
                    scale = next;
                    info.scale = Math.round(next * 100) / 100;
                    return attempt(lim, null, tries + 1);
                }
            }
            if (plan.per >= plan.pages) {
                info.slices = 1;
                run = single(m.worker);
            } else {
                dropOverlay(m);
                run = sliced(m.geo, plan);
            }
            return run.catch(function(err) {
                if (!err || err.code !== REFUSAL) throw err;
                if (tries >= 5) throw fail('blank_render', err.message);
                info.refused++;
                if (plan.per > 1) return attempt(lim, Math.floor(plan.per / 2), tries + 1);
                /* Even one page was refused: only a lower scale is left. */
                if (scale <= MIN_SCALE) throw fail('blank_render', err.message);
                scale = Math.max(MIN_SCALE, scale * 0.75);
                info.scale = Math.round(scale * 100) / 100;
                return attempt(lim, 1, tries + 1);
            });
        });
    }

    return deviceCanvasLimits().then(function(lim) {
        return attempt(lim, null, 0);
    }).then(function(blob) {
            unzero(); uncolor(); unrtl();
            if (!blob || blob.size < 800) throw fail('blank_render', 'empty pdf blob');
            if (window.__downloadFile) {
                window.__downloadFile(blob, filename);
            } else {
                var a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                setTimeout(function() { try { URL.revokeObjectURL(a.href); a.remove(); } catch (e) {} }, 4000);
            }
            var stats = { ms: Date.now() - t0, kb: Math.round(blob.size / 1024), pages: info.pages,
                slices: info.slices, refused: info.refused, fonts: info.fonts, scale: info.scale, nobold: info.nobold };
            emit('pdf_export', stats);
            return stats;
        })
        // Страховка: если съёмка упала ДО шага с холстом, восстановление выше
        // не отработало бы, и холст тула остался бы 1x1 на живой странице.
        .then(function(v) { unzero(); uncolor(); unrtl(); return v; }, function(e) { unzero(); uncolor(); unrtl(); throw e; });
}

window.__generatePDF = function(opts) {
    if (!opts || !opts.element) {
        /* Молчащий отказ здесь означает МЁРТВУЮ КНОПКУ: человек жмёт «Скачать
           PDF», не получает ни файла, ни сообщения, и пожаловаться ему не на
           что. Самая частая причина — позиционный вызов `(element, opts)`
           вместо одного объекта: так жили /jump-height, /speed-gun и
           /lyrics-writer. Форму держит сторож в суите, а это — сетка
           безопасности для того, что сторож не увидит. */
        try {
            var why = (opts && opts.nodeType === 1) ? 'element_as_options' : 'no_element';
            if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'pdf_bad_call', why: why });
            if (window.console && console.error) {
                console.error('[Timbrica] __generatePDF ждёт ОДИН объект с полем element: __generatePDF({element: el, …})');
            }
        } catch (e) {}
        return Promise.resolve({ ok: false, code: 'gen_error' });
    }

    var strings = {};
    for (var k in DEFAULT_STRINGS) {
        strings[k] = (opts.strings && opts.strings[k]) || commonString('pdf_' + k) || DEFAULT_STRINGS[k];
    }

    /* `asIs` captures the element exactly as given — no clone, no forced light
     * colors, no 800px width normalisation. It exists for sheets whose layout
     * IS the product: the ring sizer prints circles specified in millimetres
     * next to a 25 mm calibration square, and re-flowing it to 800px would
     * quietly print rings of the wrong size. Everything whose output is a
     * report should stay on the default clone path. */
    var liveMode = !!opts.onBefore || opts.asIs === true;

    var working = null;

    function cleanup() {
        if (working) { clearTimeout(working); working = null; }
        if (opts.onAfter) { try { opts.onAfter(); } catch (e) {} }
    }

    /* Hand the frame back before the heavy work starts.
     *
     * html2canvas renders on the main thread and cannot leave it — it needs
     * layout. Measured in the field over 30 days: 325 interactions across 14
     * tools sat inside a frame this file blocked for a MEDIAN of 6.9 s, p90
     * 13.8 s, max 35.4 s (LoAF `report-pdf.js|fl936|b15673`), half of them on
     * phones; the worst single interaction ran 22.7 s on /synastry. To the
     * visitor the page is simply dead: no spinner, no cursor, nothing — the
     * one caller checked (level-test-app.js) sets no busy state at all, and
     * neither do the other 72.
     *
     * The promise chain LOOKED like it already yielded, and that is why this
     * survived: `loadHtml2pdf()` resolves synchronously once the library is
     * cached, so `.then()` runs as a MICROtask — same task, no frame, no
     * paint. So the first export of a session (script still loading) behaved
     * correctly and every later one froze.
     *
     * rAF + setTimeout(0) is the yield that actually reaches a painted frame:
     * the rAF callback runs before the frame is committed, the timeout after.
     * Nothing about delivery changes — the download already happened seconds
     * of async work later, past any user activation, and goes through
     * __downloadFile either way.
     */
    function afterPaint() {
        return new Promise(function(resolve) {
            if (typeof requestAnimationFrame !== 'function') { setTimeout(resolve, 0); return; }
            requestAnimationFrame(function() { setTimeout(resolve, 0); });
        });
    }

    loadCanvasLimits();
    var p = loadHtml2pdf().catch(function(err) {
        var e = fail('lib_load', err && err.message);
        throw e;
    }).then(function() {
        /* Say it out loud — but only if there is actually a wait.
         *
         * The first version announced on every export. That is right for the
         * 6.9 s median this file used to freeze the page for, and wrong for a
         * small report on a desktop, where the toast and the "saved" toast
         * would land on top of each other for no reason. Half a second is
         * past the point where a person starts wondering whether the click
         * registered, and well short of the frames this file actually blocks.
         *
         * The metric does not depend on the toast: the yield below hands back
         * a painted frame either way. */
        working = setTimeout(function () {
            if (strings.working && window.__toast) window.__toast(strings.working, 'info');
            try { window.__announce && window.__announce(strings.working); } catch (e) {}
        }, 500);

        return afterPaint();
    }).then(function() {
        var target;
        if (liveMode) {
            // `asIs` reaches this branch without an onBefore of its own.
            if (opts.onBefore) opts.onBefore();
            target = opts.element;
        } else {
            /* Default approach: clone the element, lay it out at the real
             * page width, and hand html2pdf a DETACHED node — it wraps the
             * source in its own container, and an attached
             * position:fixed/absolute wrapper collapses that container to
             * scrollHeight 0 => a blank canvas. The v1 "position in viewport"
             * styling was exactly that bug. The clone is attached only for the
             * measuring pass inside createPdfClone, off-screen, and comes back
             * detached. */
            target = createPdfClone(opts.element, opts.title, opts);
        }
        return renderAndDeliver(target, opts);
    });

    return p.then(function(stats) {
        cleanup();
        if (strings.saved && window.__toast) window.__toast(strings.saved, 'success');
        return { ok: true, ms: stats && stats.ms, kb: stats && stats.kb };
    }, function(err) {
        cleanup();
        var code = (err && err.code) || 'gen_error';
        // ── Причина отказа обязана быть ЧИТАЕМОЙ, а не числом ────────────
        // `err.code` — это либо наш ярлык ('lib_load', 'blank_render', …),
        // либо ЧУЖОЙ числовой код DOMException, и тогда в телеметрию уезжает
        // голое «11». Разбор 10.09.2026: у /depression-test 94 таких отказа
        // за 90 дней против 12 успехов, отдельные сессии — 22 нажатия и ни
        // одной выдачи, и по числу «11» невозможно сказать даже, что именно
        // бросило (проверены и опровергнуты замером: нулевой холст, битая
        // картинка, чужой iframe, SVG без размеров — html2canvas переживает
        // всё это молча). Имя и текст ошибки стоят 60 байт и превращают
        // следующее расследование в чтение.
        var _why = '';
        try {
            _why = String((err && (err.name || '')) + ': ' + (err && (err.message || err))).slice(0, 160);
        } catch (e) {}
        emit('pdf_export_fail', { reason: code, why: _why });
        try { console.error('PDF error:', err); } catch (e) {}
        if (window.__toast) {
            window.__toast(code === 'canvas_blocked' ? strings.blocked
                : code === 'lib_load' ? strings.libError
                : strings.genError, 'error');
        }
        return { ok: false, code: code };
    });
};

})();
